import { InfoOutlined, Replay } from '@mui/icons-material';
import { Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FieldConfidence } from 'routes/LpPages/IncomeAndExpenses/AddEditIncomeOrExpense/AiFormFillContext';
import { FC, PropsWithChildren } from 'react';
import { Column, Row } from 'further-ui/components/Layout';
import GradientAiIcon from 'components/icons/GradientAiIcon';

const AI_GRADIENT = 'linear-gradient(to bottom, #E91E63, #2196F3)';

type Props = PropsWithChildren<{
  confidence?: FieldConfidence;
  reason?: string;
  isModified?: boolean;
  onRevert?: () => void;
}>;

const GRADIENT_BORDER_BG = `linear-gradient(white, white) padding-box, ${AI_GRADIENT} border-box`;

// Apply the gradient border to the root wrapper (not the fieldset).
// The fieldset (.MuiOutlinedInput-notchedOutline) is position:absolute and
// painted on top of the input — giving it a white padding-box fill hides the text.
// Instead we put the border on the root and suppress the fieldset border in all
// states (default, hover, focus) to prevent MUI's own rules from re-showing it.
const InputContainer = styled('div')({
  '& .MuiOutlinedInput-root': {
    border: '1px solid transparent',
    background: GRADIENT_BORDER_BG,
    '&:hover, &.Mui-focused': {
      border: '1px solid transparent',
      background: GRADIENT_BORDER_BG,
    },
  },
  '& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
    {
      border: 'none',
    },
});

const WarningInputContainer = styled('div')(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    border: `1px solid ${theme.palette.warning.light}`,
    '&:hover, &.Mui-focused': {
      border: `1px solid ${theme.palette.warning.light}`,
    },
  },
  '& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
    {
      border: 'none',
    },
}));

const HintInfoIcon = styled(InfoOutlined)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.disabled,
  cursor: 'help',
}));

const RevertButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  color: '#9e9e9e',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  padding: 0,
  '&:hover': { opacity: 0.7 },
});

const AiFieldHintWrapper: FC<Props> = ({
  confidence,
  reason,
  isModified,
  onRevert,
  children,
}) => {
  if (!confidence) {
    return <>{children}</>;
  }

  if (isModified && onRevert) {
    return (
      <Column spacing="xs">
        {children}
        <RevertButton
          type="button"
          onClick={onRevert}
          aria-label="Revert to agent answer"
        >
          <Row spacing="xs" centered>
            <Replay sx={{ fontSize: 16 }} />
            <Typography variant="body2">Revert to agent answer</Typography>
          </Row>
        </RevertButton>
      </Column>
    );
  }

  const isHighConfidence = confidence === 'high';

  return (
    <Column spacing="xs">
      {isHighConfidence ? (
        <InputContainer>{children}</InputContainer>
      ) : (
        <WarningInputContainer>{children}</WarningInputContainer>
      )}
      <Row spacing="xs" centered>
        <GradientAiIcon size={16} grey={!isHighConfidence} />
        <Typography variant="body2">
          {isHighConfidence ? 'Agent suggestion' : 'Needs attention'}
        </Typography>
        {!isHighConfidence && reason && (
          <Tooltip title={reason} placement="top">
            <HintInfoIcon />
          </Tooltip>
        )}
      </Row>
    </Column>
  );
};

export default AiFieldHintWrapper;
